import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useTheme, type Theme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import { flattenNav, isNavGroup, navigation, type NavEntry, type NavGroup, type NavItem } from '@/navigation/navItems';
import { useUiStore } from '@/store/uiStore';
import { useAuthStore } from '@/auth/authStore';
import { useModuleStore } from '@/core/modules/moduleStore';
import ProductMark from '@/components/common/ProductMark';
import { useT } from '@/i18n/i18n';

const appName = import.meta.env.VITE_APP_NAME ?? 'Annova ERP Plus';

export const DRAWER_WIDTH = 264;

function SidebarContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const navGroupsOpen = useUiStore((s) => s.navGroupsOpen);
  const setNavGroupOpen = useUiStore((s) => s.setNavGroupOpen);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const enabledModules = useModuleStore((s) => s.enabled);
  const features = useModuleStore((s) => s.features);
  // The menu reads in the person's language where a pack translates it (docs §259).
  const t = useT();

  const canSee = (item: NavItem) =>
    (!item.module || enabledModules[item.module] === true)
    && (!item.feature || features[item.feature] === true)
    && (item.anyPermission ? hasAnyPermission(...item.anyPermission) : hasPermission(item.permission));
  // The selected item is the one whose path matches the location best: exactly, or as the
  // longest prefix, so /accounting/journals lights Journals and not the Overview at /accounting.
  const matches = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(`${path}/`));
  // A tab with no menu item of its own lights the item of a sibling tab in the same section
  // (/hr/people/x lights an item at /hr/people/employees) rather than falling back to the overview.
  const score = (path: string) => {
    if (matches(path)) return path.length;
    const section = path.slice(0, path.lastIndexOf('/'));
    return section.split('/').length > 2 && matches(section) ? section.length + 0.5 : -1;
  };
  const activePath = flattenNav(navigation)
    .map((item) => ({ path: item.path, score: score(item.path) }))
    .filter((m) => m.score >= 0)
    .sort((a, b) => b.score - a.score)[0]?.path;
  const isSelected = (item: NavItem) => item.path === activePath;
  const holds = (group: NavGroup): boolean => group.items.some((e) => (isNavGroup(e) ? holds(e) : isSelected(e)));

  // Groups with nothing the person may see are left out altogether, at every depth.
  const prune = (entries: readonly NavEntry[]): NavEntry[] => entries
    .map((entry) => (isNavGroup(entry) ? { ...entry, items: prune(entry.items) } : entry))
    .filter((entry) => (isNavGroup(entry) ? entry.items.length > 0 : canSee(entry)));
  const visible = prune(navigation);

  // Every group on the path to the current page opens when the person arrives on it; what they open
  // or close by hand afterwards is remembered.
  const groupsHoldingCurrent = (entries: readonly NavEntry[]): NavGroup[] =>
    entries.flatMap((e) => (isNavGroup(e) && holds(e) ? [e, ...groupsHoldingCurrent(e.items)] : []));
  const currentGroups = groupsHoldingCurrent(visible);
  useEffect(() => {
    for (const group of currentGroups) if (navGroupsOpen[group.key] !== true) setNavGroupOpen(group.key, true);
    // Only when the page changes: reopening on every store change would undo a deliberate close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Styling comes from the theme's chrome tokens. Fluent: a flat list with a blue accent bar over
  // a light blue fill when selected. Outlook 2007: every link is a bordered gradient button — pale
  // blue at rest, orange when selected.
  // Each level steps in a little further; the depth is how many groups sit above the entry.
  const buttonSx = (depth: number) => ({
    borderRadius: 1,
    mb: (t: Theme) => t.spacing(t.palette.chrome.navItemSpacing),
    pl: 2 + depth * 1.5,
    color: (t: Theme) => t.palette.chrome.navText,
    '& .MuiListItemIcon-root': { color: 'inherit' },
    background: (t: Theme) => t.palette.chrome.navItemBackground,
    border: (t: Theme) => `1px solid ${t.palette.chrome.navItemBorder}`,
    borderLeft: (t: Theme) => `3px solid ${t.palette.chrome.navItemBorder}`,
    '&.Mui-selected': {
      borderColor: (t: Theme) => t.palette.chrome.navSelectedBorder,
      borderLeftColor: (t: Theme) => t.palette.chrome.navSelectedAccent,
      background: (t: Theme) => t.palette.chrome.navSelectedBackground,
      color: (t: Theme) => t.palette.chrome.navSelectedText,
      '& .MuiListItemIcon-root': { color: (t: Theme) => t.palette.chrome.navSelectedAccent },
      '&:hover': { background: (t: Theme) => t.palette.chrome.navSelectedHoverBackground },
    },
  });

  const renderItem = (item: NavItem, depth: number) => {
    const selected = isSelected(item);
    const Icon = item.icon;
    return (
      <ListItemButton key={item.path} selected={selected} onClick={() => handleNavigate(item.path)} sx={buttonSx(depth)}>
        <ListItemIcon sx={{ minWidth: depth > 0 ? 34 : 40 }}>
          <Icon fontSize='small' color='inherit' />
        </ListItemIcon>
        <ListItemText primary={t(item.label)} primaryTypographyProps={{ fontSize: depth > 0 ? 13.5 : 14, fontWeight: selected ? 600 : 500 }} />
        {item.comingSoon && (
          <Chip label="Soon" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
        )}
      </ListItemButton>
    );
  };

  const renderEntry = (entry: NavEntry, depth: number) => (isNavGroup(entry) ? renderGroup(entry, depth) : renderItem(entry, depth));

  const renderGroup = (group: NavGroup, depth: number) => {
    const holdsCurrent = holds(group);
    const open = navGroupsOpen[group.key] ?? holdsCurrent;
    const Icon = group.icon;
    return (
      <Box key={group.key}>
        <ListItemButton onClick={() => setNavGroupOpen(group.key, !open)} sx={buttonSx(depth)}
          aria-expanded={open} aria-controls={`nav-group-${group.key}`}>
          <ListItemIcon sx={{ minWidth: depth > 0 ? 34 : 40 }}>
            <Icon fontSize='small' color='inherit' />
          </ListItemIcon>
          <ListItemText primary={t(group.label)} primaryTypographyProps={{ fontSize: depth > 0 ? 13.5 : 14, fontWeight: holdsCurrent && !open ? 700 : 600 }} />
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit id={`nav-group-${group.key}`}>
          <List disablePadding sx={{ mb: 0.5 }}>
            {group.items.map((item) => renderEntry(item, depth + 1))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Box sx={{ mr: 1.25 }}><ProductMark size={34} /></Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {appName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Strategy analytics
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1.5, py: 1, flexGrow: 1, overflowY: 'auto' }}>
        {visible.map((entry) => renderEntry(entry, 0))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} {appName}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Sidebar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <SidebarContent />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      open={sidebarOpen}
      sx={{
        width: sidebarOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: sidebarOpen
            ? theme.transitions.duration.enteringScreen
            : theme.transitions.duration.leavingScreen,
        }),
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <SidebarContent />
    </Drawer>
  );
}
